<template>
  <donut-form-item
    label="Set my working hours"
    :left-align="true"
  >
    <template #description>
      Donut will suggest meeting times within the hours you choose, in your Slack account's time zone: <a :href="timezoneSettingsUrl" target="_blank" class="link">{{ timezone }}</a>.
    </template>
    <div class="f-h f-ac f-sb">
      <div class="f-h f-ac w-50p m-rx2">
        <div class="t-sm m-rx2">Work begins</div>
        <div class="flexible">
          <donut-selector-single-static
            v-model="beginningSelection"
            :options="timeOptions"
            :key-map="keyMap"
            placeholder="Select a time"
            :loading="loading"
            :update-function="(option) => updateWorkingHour('beginning', option)"
            class="w-100p"
          />
        </div>
      </div>
      <div class="f-h f-ac w-50p m-lx2">
        <div class="t-sm m-rx2">Work ends</div>
        <div class="flexible">
          <donut-selector-single-static
            v-model="endSelection"
            :options="timeOptions"
            :key-map="keyMap"
            placeholder="Select a time"
            :loading="loading"
            :update-function="(option) => updateWorkingHour('end', option)"
            class="w-100p"
          />
        </div>
      </div>
    </div>
  </donut-form-item>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';

const { state, dispatch, getters, commit } = useStore();

const keyMap = { label: 'label', value: 'value' };

// 24 hours, 15-minute increments
const timeOptions = computed(() => {
  const options = [];
  for (let hour24 = 0; hour24 < 24; hour24 += 1) {
    for (let minute = 0; minute < 60; minute += 15) {
      const mm = minute.toString().padStart(2, '0');
      const hour12 = (hour24 % 12) === 0 ? 12 : (hour24 % 12);
      const suffix = hour24 < 12 ? 'am' : 'pm';
      const label = `${hour12}:${mm} ${suffix}`; // e.g., "1:00 am", "12:15 pm"
      const value = `${hour24}:${mm}`; // e.g., "0:00", "13:15"
      options.push({ label, value });
    }
  }
  return options;
});

const endTime = computed({
  get: () => getters['user/endTime'],
  set: (value) => {
    commit('user/update', { key: 'endTime', value });
  },
});

const beginningTime = computed({
  get: () => getters['user/beginningTime'],
  set: (value) => {
    commit('user/update', { key: 'beginningTime', value });
  },
});

// Map string value to selector option object and vice versa
const findOptionByValue = (val) => timeOptions.value.find((o) => o.value === val) || null;

const beginningSelection = computed({
  get: () => (beginningTime.value ? findOptionByValue(beginningTime.value) : null),
  set: (option) => {
    const value = option ? option.value : '';
    beginningTime.value = value;
  },
});

const endSelection = computed({
  get: () => (endTime.value ? findOptionByValue(endTime.value) : null),
  set: (option) => {
    const value = option ? option.value : '';
    endTime.value = value;
  },
});

const loading = computed(() => getters['user/loading']);

const updateWorkingHour = (which, option) => {
  window.mixpanel.track('User Web Settings - Working Hours - Updated', {
    which,
    option,
  });
  const value = option ? option.value : '';
  const currentValue = which === 'beginning' ? getters['user/beginningTime'] : getters['user/endTime'];
  if (value === currentValue) {
    return; // No change, don't update
  }
  if (which === 'beginning') {
    commit('user/update', { key: 'beginningTime', value });
  } else {
    commit('user/update', { key: 'endTime', value });
  }
  dispatch('user/updateWorkingHours', {
    userId: state.currentUser.id,
    beginningTime: getters['user/beginningTime'],
    endTime: getters['user/endTime'],
  });
};

const timezone = computed(() => state.currentUser?.time_zone_info?.value || 'your timezone');
const timezoneSettingsUrl = computed(() => `https://${state.currentTeam.url}.slack.com/account/settings#timezone`);

onMounted(() => {
  commit('user/update', { key: 'beginningTime', value: state.currentUser?.beginning_of_workday || '0:00' });
  commit('user/update', { key: 'endTime', value: state.currentUser?.end_of_workday || '0:00' });
});
</script>
